import * as THREE from 'three'
import { HAND_LM, type HandMetric, type Vec3 } from '@ar-suite/core'
import { createOccluderMaterial } from './occluder'

/** Bone segments: [a, b, radiusCm]. Palm bones are thicker; finger radii taper. */
const BONES: [number, number, number][] = [
  // palm fan
  [HAND_LM.WRIST, HAND_LM.INDEX_MCP, 1.5],
  [HAND_LM.WRIST, HAND_LM.MIDDLE_MCP, 1.6],
  [HAND_LM.WRIST, HAND_LM.RING_MCP, 1.6],
  [HAND_LM.WRIST, HAND_LM.PINKY_MCP, 1.4],
  [HAND_LM.INDEX_MCP, HAND_LM.PINKY_MCP, 1.4],
  // thumb
  [HAND_LM.WRIST, HAND_LM.THUMB_CMC, 1.3],
  [HAND_LM.THUMB_CMC, HAND_LM.THUMB_MCP, 1.1],
  [HAND_LM.THUMB_MCP, HAND_LM.THUMB_IP, 0.95],
  [HAND_LM.THUMB_IP, HAND_LM.THUMB_TIP, 0.85],
  // fingers (proximal → tip)
  [HAND_LM.INDEX_MCP, HAND_LM.INDEX_PIP, 0.85],
  [HAND_LM.INDEX_PIP, HAND_LM.INDEX_DIP, 0.75],
  [HAND_LM.INDEX_DIP, HAND_LM.INDEX_TIP, 0.68],
  [HAND_LM.MIDDLE_MCP, HAND_LM.MIDDLE_PIP, 0.88],
  [HAND_LM.MIDDLE_PIP, HAND_LM.MIDDLE_DIP, 0.78],
  [HAND_LM.MIDDLE_DIP, HAND_LM.MIDDLE_TIP, 0.7],
  [HAND_LM.RING_MCP, HAND_LM.RING_PIP, 0.82],
  [HAND_LM.RING_PIP, HAND_LM.RING_DIP, 0.72],
  [HAND_LM.RING_DIP, HAND_LM.RING_TIP, 0.65],
  [HAND_LM.PINKY_MCP, HAND_LM.PINKY_PIP, 0.7],
  [HAND_LM.PINKY_PIP, HAND_LM.PINKY_DIP, 0.62],
  [HAND_LM.PINKY_DIP, HAND_LM.PINKY_TIP, 0.55],
]

const _a = new THREE.Vector3()
const _b = new THREE.Vector3()
const _mid = new THREE.Vector3()
const _dir = new THREE.Vector3()
const _q = new THREE.Quaternion()
const UP = new THREE.Vector3(0, 1, 0)

/**
 * Depth-only hand occluder: one capsule per bone, driven by the metric hand points every frame.
 * Radii scale with the measured knuckle span so bigger/smaller hands occlude correctly.
 */
export class HandOccluder {
  readonly group = new THREE.Group()
  private bones: THREE.Mesh<THREE.CylinderGeometry, THREE.MeshBasicMaterial>[] = []
  private joints: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>[] = []
  private forearm: THREE.Mesh<THREE.CylinderGeometry, THREE.MeshBasicMaterial>
  private _debug = false

  constructor() {
    this.group.name = 'hand-occluder'
    const mat = createOccluderMaterial()
    // exact-size primitives: unit cylinder (r=1, h=1) scaled to (radius, boneLength, radius),
    // plus a sphere at every landmark to round the joints — no capsule height inflation.
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 10, 1)
    const sph = new THREE.SphereGeometry(1, 10, 8)
    for (let i = 0; i < BONES.length; i++) {
      const m = new THREE.Mesh(cyl, mat)
      m.renderOrder = -100
      m.frustumCulled = false
      m.matrixAutoUpdate = false
      this.bones.push(m)
      this.group.add(m)
    }
    for (let i = 0; i < 21; i++) {
      const m = new THREE.Mesh(sph, mat)
      m.renderOrder = -100
      m.frustumCulled = false
      m.matrixAutoUpdate = false
      this.joints.push(m)
      this.group.add(m)
    }
    // the forearm continues past the wrist — without it, nothing occludes bangles/watch straps
    this.forearm = new THREE.Mesh(cyl, mat)
    this.forearm.renderOrder = -100
    this.forearm.frustumCulled = false
    this.forearm.matrixAutoUpdate = false
    this.group.add(this.forearm)
    this.group.visible = false
  }

  get debug(): boolean {
    return this._debug
  }
  set debug(v: boolean) {
    if (v === this._debug) return
    this._debug = v
    const mat = createOccluderMaterial(v)
    if (v) mat.color.set(0x33dd88)
    for (const c of this.bones) c.material = mat
    for (const c of this.joints) c.material = mat
    this.forearm.material = mat
  }

  /**
   * Update from a lifted hand; pass null when the hand is not tracked.
   * @param forearmDir unit direction from the wrist toward the elbow (camera space) — extends
   *                   a forearm occluder so wrist wearables hide behind the real arm.
   */
  update(hand: HandMetric | null, forearmDir?: Vec3 | null): void {
    if (!hand) {
      this.group.visible = false
      return
    }
    this.group.visible = true
    const scale = hand.knuckleSpanCm / 8.2 // canonical span the radii were authored for
    const jointR = new Float32Array(21).fill(0)
    for (let i = 0; i < BONES.length; i++) {
      const [ai, bi, r0] = BONES[i]!
      const r = r0 * scale
      const c = this.bones[i]!
      _a.set(hand.points[ai * 3]!, hand.points[ai * 3 + 1]!, hand.points[ai * 3 + 2]!)
      _b.set(hand.points[bi * 3]!, hand.points[bi * 3 + 1]!, hand.points[bi * 3 + 2]!)
      _mid.addVectors(_a, _b).multiplyScalar(0.5)
      _dir.subVectors(_b, _a)
      const len = _dir.length()
      _q.setFromUnitVectors(UP, _dir.normalize())
      c.matrix.compose(_mid, _q, new THREE.Vector3(r, Math.max(len, 0.1), r))
      c.matrixWorldNeedsUpdate = true
      jointR[ai] = Math.max(jointR[ai]!, r)
      jointR[bi] = Math.max(jointR[bi]!, r)
    }
    for (let i = 0; i < 21; i++) {
      const j = this.joints[i]!
      const r = jointR[i]! || 0.01
      j.matrix.makeScale(r, r, r)
      j.matrix.setPosition(hand.points[i * 3]!, hand.points[i * 3 + 1]!, hand.points[i * 3 + 2]!)
      j.matrixWorldNeedsUpdate = true
    }
    // forearm: from just before the wrist, 30 cm toward the elbow, slightly slimmer than the wrist
    if (forearmDir) {
      const w = HAND_LM.WRIST
      _a.set(hand.points[w * 3]!, hand.points[w * 3 + 1]!, hand.points[w * 3 + 2]!)
      _dir.set(forearmDir[0], forearmDir[1], forearmDir[2]).normalize()
      const LEN = 30
      _mid.copy(_a).addScaledVector(_dir, LEN / 2 - 0.5)
      _q.setFromUnitVectors(UP, _dir)
      const r = hand.knuckleSpanCm * 0.30
      this.forearm.matrix.compose(_mid, _q, new THREE.Vector3(r, LEN, r))
      this.forearm.matrixWorldNeedsUpdate = true
      this.forearm.visible = true
    } else {
      this.forearm.visible = false
    }
  }

  dispose(): void {
    this.bones[0]?.geometry.dispose()
    this.joints[0]?.geometry.dispose()
    this.bones[0]?.material.dispose()
  }
}
